import React, { useState } from 'react';
import { Accordion, Icon, Button, Message, List, Label } from 'semantic-ui-react';

interface ArgumentCategory {
  title: string;
  icon: string;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'teal' | 'red';
  phrases: string[];
}

const SalesArgumentsPanel: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<string | null>(null);

  const argumentCategories: ArgumentCategory[] = [
    {
      title: 'Já tenho consultor',
      icon: 'user',
      color: 'blue',
      phrases: [
        'Sem problema. O ponto é que a fidelidade vai encerrar. Quando isso acontece, o sistema libera o plano para ajuste automático. Estou te acionando agora porque ainda dá tempo de reaver os valores e planos, depois disso não tem como voltar atrás.'
      ]
    },
    {
      title: 'Está mais caro do que estou pagando',
      icon: 'dollar sign',
      color: 'green',
      phrases: [
        'Esse valor é exatamente porque esta em um plano de 2 anos atrás desatualizado, que não tem mais disponível na operadora. Se deixar passar, o plano sai dessa condição e o valor muda. Por isso estou te trazendo agora, por isso estou te ligando enquanto ainda da tempo de renegociar.'
      ]
    },
    {
      title: 'Já renovei (mas não está renovado)',
      icon: 'refresh',
      color: 'purple',
      phrases: [
        'Se estivesse renovado, a fidelidade já apareceria ativa no sistema. Como não está, significa que o plano está livre para ajuste. Se não regularizar agora, o próximo faturamento já entra com valor diferente.'
      ]
    },
    {
      title: 'Meu plano já me atende, não quero renovar',
      icon: 'check circle',
      color: 'orange',
      phrases: [
        'Entendo, mas não renovar não mantém o plano como está. Sem atualização o sistema entende que o plano pode ser reajustado a qualquer momento. A renovação é o que segura a condição atual.'
      ]
    },
    {
      title: 'Não quero fidelidade',
      icon: 'lock',
      color: 'teal',
      phrases: [
        'Sem fidelidade, o plano continua, mas sem nenhuma proteção de valor. A fidelidade é o único mecanismo que trava preço e benefício. Sem ela, o aumento não depende de aviso.'
      ]
    },
    {
      title: 'Vou trocar de operadora, a TIM só reajusta',
      icon: 'exchange',
      color: 'red',
      phrases: [
        'A troca não elimina fidelidade nem reajuste. A diferença é que aqui ainda dá tempo de renovar e travar a condição. Se não fizer agora, o ajuste acontece antes mesmo de qualquer migração.'
      ]
    }
  ];

  const handleAccordionClick = (index: number) => {
    setActiveIndex(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const copyToClipboard = async (text: string, categoryIndex: number, phraseIndex: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(`${categoryIndex}-${phraseIndex}`);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Message info style={{ marginBottom: '24px', borderRadius: '8px' }}>
        <Message.Header style={{ fontSize: '17px' }}>
          <Icon name="info circle" />
          Frases de Argumentação
        </Message.Header>
        <p style={{ fontSize: '16px', marginTop: '10px' }}>
          Use estas frases para ajudar na comunicação com o cliente. 
          Clique no botão de copiar para usar a frase desejada. Você pode expandir múltiplas categorias simultaneamente.
        </p>
      </Message>

      <Accordion styled fluid>
        {argumentCategories.map((category, catIndex) => {
          const isActive = activeIndex.includes(catIndex);
          
          return (
            <React.Fragment key={catIndex}>
              <Accordion.Title
                active={isActive}
                index={catIndex}
                onClick={() => handleAccordionClick(catIndex)}
                style={{
                  cursor: 'pointer',
                  padding: '16px 20px',
                  backgroundColor: isActive ? '#f0f0f0' : '#ffffff',
                  borderLeft: `4px solid var(--${category.color})`,
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Icon 
                    name={isActive ? 'chevron down' : 'chevron right'} 
                    style={{ color: category.color === 'blue' ? '#2185d0' : 
                                    category.color === 'green' ? '#21ba45' :
                                    category.color === 'purple' ? '#a333c8' :
                                    category.color === 'orange' ? '#f2711c' :
                                    category.color === 'teal' ? '#00b5ad' : '#db2828' }}
                  />
                  <Icon 
                    name={category.icon as any} 
                    color={category.color}
                    style={{ marginRight: '8px' }}
                  />
                  <strong style={{ fontSize: '18px', color: '#2c3e50' }}>
                    {category.title}
                  </strong>
                  <Label 
                    color={category.color} 
                    size="small" 
                    style={{ marginLeft: 'auto', fontSize: '14px' }}
                  >
                    {category.phrases.length} frases
                  </Label>
                </div>
              </Accordion.Title>
              
              <Accordion.Content active={isActive} style={{ padding: '0 20px 20px 20px' }}>
                <List divided relaxed style={{ marginTop: '12px' }}>
                  {category.phrases.map((phrase, phraseIndex) => {
                    const indexKey = `${catIndex}-${phraseIndex}`;
                    const isCopied = copiedIndex === indexKey;
                    
                    return (
                      <List.Item key={phraseIndex} style={{ padding: '16px 0', borderBottom: '1px solid #e0e0e0' }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          gap: '16px'
                        }}>
                          <div style={{ flex: 1 }}>
                            <p style={{ 
                              margin: 0, 
                              color: '#2c3e50',
                              fontSize: '18px',
                              lineHeight: '1.8',
                              fontStyle: 'italic'
                            }}>
                              "{phrase}"
                            </p>
                          </div>
                          <Button
                            size="small"
                            icon={isCopied ? 'check' : 'copy'}
                            color={isCopied ? 'green' : 'grey'}
                            onClick={() => copyToClipboard(phrase, catIndex, phraseIndex)}
                            style={{ 
                              minWidth: '100px',
                              borderRadius: '6px',
                              transition: 'all 0.2s ease',
                              fontSize: '14px'
                            }}
                            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                              if (!isCopied) {
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }
                            }}
                            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            {isCopied ? 'Copiado!' : 'Copiar'}
                          </Button>
                        </div>
                      </List.Item>
                    );
                  })}
                </List>
              </Accordion.Content>
            </React.Fragment>
          );
        })}
      </Accordion>
    </div>
  );
};

export default SalesArgumentsPanel;

